import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import _, { trim, toLower, includes, find } from 'lodash'
import CityLocationPicker from './CityLocationPicker'

const AddPointOfInterestModal = ({ onSuccess, close, id = null, onMapUiLanguageChange }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [countries, setCountries] = useState([])
  const [mapOpen, setMapOpen] = useState(false)
  const [mapLanguage, setMapLanguage] = useState('en')
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    country: '',
    lat: '',
    lon: '',
    active: true,
  })

  useEffect(() => {
    fetchCountries()
    if (id) {
      fetchItem()
    }
  }, [id])

  const selectedCountry = useMemo(() => {
    if (!formData.country) return null
    return find(countries, item => String(item.id) === String(formData.country)) || null
  }, [countries, formData.country])

  const selectedCountryCode = selectedCountry?.code || ''

  const fetchCountries = async () => {
    try {
      const [bookingResponse, countriesResponse] = await Promise.all([apiGet('booking-countries'), apiGet('countries', { pageSize: 500, pageNumber: 1 })])
      const bookingList = _.isArray(bookingResponse?.data?.countries) ? bookingResponse.data.countries : []
      const codeByName = {}
      _.forEach(bookingList, item => {
        if (item?.name) codeByName[toLower(item.name)] = toLower(item.code || '')
        if (item?.nameAr) codeByName[toLower(item.nameAr)] = toLower(item.code || '')
      })

      if (countriesResponse?.data?.countries) {
        setCountries(
          countriesResponse.data.countries
            .filter(c => c.active !== false && codeByName[toLower(c.country || '')])
            .map(c => ({
              id: c._id,
              name: isArabic && c.countryAr ? c.countryAr : c.country,
              englishName: c.country,
              code: codeByName[toLower(c.country || '')],
            }))
        )
      } else {
        setCountries([])
      }
    } catch (error) {
      setCountries([])
    }
  }

  const fetchItem = async () => {
    try {
      const response = await apiGet(`point-of-interest/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          name: data.name || '',
          nameAr: data.nameAr || '',
          description: data.description || '',
          descriptionAr: data.descriptionAr || '',
          country: data.country?._id || data.country || '',
          lat: data.lat != null ? String(data.lat) : '',
          lon: data.lon != null ? String(data.lon) : '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addPointOfInterestModal.messages.failedToFetch'))
      }
    } catch (error) {
      notify.error(t('addPointOfInterestModal.messages.failedToFetch'))
    }
  }

  const closeMapPicker = () => {
    setMapOpen(false)
    onMapUiLanguageChange?.(null)
  }

  const openMapPicker = language => {
    if (!formData.country) {
      notify.error(t('addPointOfInterestModal.messages.selectCountryBeforeLocation'))
      return
    }
    setMapLanguage(language)
    setMapOpen(true)
    onMapUiLanguageChange?.(language)
  }

  const handleCountryChange = selected => {
    const countryId = selected?.id || ''
    setFormData(prev => ({
      ...prev,
      country: countryId,
      lat: countryId === prev.country ? prev.lat : '',
      lon: countryId === prev.country ? prev.lon : '',
    }))
    if (countryId) {
      const language = isArabic ? 'ar' : 'en'
      setMapLanguage(language)
      setMapOpen(true)
      onMapUiLanguageChange?.(language)
    }
  }

  const handleLocationSelect = location => {
    setFormData(prev => ({
      ...prev,
      lat: location.lat != null ? String(location.lat) : prev.lat,
      lon: location.lon != null ? String(location.lon) : prev.lon,
    }))
    closeMapPicker()
  }

  const validateCoordinates = () => {
    const lat = parseFloat(formData.lat)
    const lon = parseFloat(formData.lon)
    if (formData.lat === '' || formData.lon === '' || Number.isNaN(lat) || Number.isNaN(lon)) {
      notify.error(t('addPointOfInterestModal.messages.coordinatesRequired'))
      return null
    }
    return { lat, lon }
  }

  const handleSubmit = async () => {
    if (!formData.country) {
      notify.error(t('addPointOfInterestModal.messages.countryRequired'))
      return
    }
    if (!trim(formData.name)) {
      notify.error(t('addPointOfInterestModal.messages.nameRequired'))
      return
    }
    if (!trim(formData.nameAr)) {
      notify.error(t('addPointOfInterestModal.messages.nameArRequired'))
      return
    }
    const coordinates = validateCoordinates()
    if (!coordinates) return

    const payload = {
      type: 'delivery',
      name: formData.name,
      nameAr: formData.nameAr,
      description: formData.description,
      descriptionAr: formData.descriptionAr,
      country: formData.country,
      active: formData.active,
      lat: coordinates.lat,
      lon: coordinates.lon,
    }

    try {
      const response = id ? await apiPut(`point-of-interest/${id}`, payload) : await apiPost('point-of-interest', payload)
      if (response.success) {
        notify.success(id ? t('addPointOfInterestModal.messages.updated') : t('addPointOfInterestModal.messages.added'))
        onSuccess()
        close()
      } else {
        const message = toLower(response.message || '')
        const isDuplicate = includes(message, 'already exists') || includes(message, 'duplicate')
        notify.error(isDuplicate ? t('addPointOfInterestModal.messages.alreadyExists') : response.message || t('addPointOfInterestModal.messages.operationFailed'))
      }
    } catch (error) {
      notify.error(t('addPointOfInterestModal.messages.failedToSave'))
    }
  }

  if (mapOpen) {
    return (
      <CityLocationPicker
        key={`${mapLanguage}-${selectedCountryCode || formData.country}`}
        language={mapLanguage}
        countryCode={selectedCountryCode}
        countryName={selectedCountry?.englishName || selectedCountry?.name}
        initialLat={formData.lat ? parseFloat(formData.lat) : undefined}
        initialLon={formData.lon ? parseFloat(formData.lon) : undefined}
        onLocationSelect={handleLocationSelect}
        onClose={closeMapPicker}
      />
    )
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addPointOfInterestModal.labels.country')}</label>
        <Selection
          onChange={handleCountryChange}
          title={t('addPointOfInterestModal.placeholders.selectCountry')}
          list={countries}
          value={formData.country || ''}
          className="w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addPointOfInterestModal.labels.name')}</label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
          placeholder={t('addPointOfInterestModal.placeholders.name')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addPointOfInterestModal.labels.nameAr')}</label>
        <input
          type="text"
          dir="rtl"
          value={formData.nameAr}
          onChange={e => setFormData(prev => ({ ...prev, nameAr: e.target.value }))}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm text-right"
          placeholder={t('addPointOfInterestModal.placeholders.nameAr')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addPointOfInterestModal.labels.description')}</label>
        <input
          type="text"
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
          placeholder={t('addPointOfInterestModal.placeholders.description')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('addPointOfInterestModal.labels.descriptionAr')}</label>
        <input
          type="text"
          dir="rtl"
          value={formData.descriptionAr}
          onChange={e => setFormData(prev => ({ ...prev, descriptionAr: e.target.value }))}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm text-right"
          placeholder={t('addPointOfInterestModal.placeholders.descriptionAr')}
        />
      </div>
      <div>
        <button
          type="button"
          onClick={() => openMapPicker(isArabic ? 'ar' : 'en')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left cursor-pointer hover:border-dark-500 sm:text-sm"
        >
          {formData.lat && formData.lon ? `${formData.lat}, ${formData.lon}` : t('addPointOfInterestModal.placeholders.pickLocation')}
        </button>
      </div>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{t('addPointOfInterestModal.labels.active')}</label>
        <Switch value={formData.active} onChange={checked => setFormData(prev => ({ ...prev, active: checked }))} />
      </div>
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={close} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          {t('addPointOfInterestModal.buttons.cancel')}
        </button>
        <button type="button" onClick={handleSubmit} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-dark-600 hover:bg-dark-700">
          {id ? t('addPointOfInterestModal.buttons.update') : t('addPointOfInterestModal.buttons.add')}
        </button>
      </div>
    </div>
  )
}

export default AddPointOfInterestModal
